import { snapdom, SnapdomPlugin } from '@zumer/snapdom';
import { ExportAction, ExportResult } from 'types/apis/cxEditor';

const imageDataUrlCache = new Map<string, string | null>();
const inFlightImageDataUrlCache = new Map<string, Promise<string | null>>();

async function resolveImageAsDataUrl(
  src: string,
  fallbackFetch?: (src: string) => Promise<string | null>,
): Promise<string | null> {
  if (!src || src.startsWith('data:')) return src;

  if (imageDataUrlCache.has(src)) {
    return imageDataUrlCache.get(src) ?? null;
  }

  const inFlight = inFlightImageDataUrlCache.get(src);
  if (inFlight) {
    return inFlight;
  }

  const task = (async () => {
    if (!fallbackFetch) return null;
    return fallbackFetch(src);
  })();

  inFlightImageDataUrlCache.set(src, task);

  try {
    const dataUrl = await task;
    imageDataUrlCache.set(src, dataUrl);
    return dataUrl;
  } finally {
    inFlightImageDataUrlCache.delete(src);
  }
}

/** Extracts URLs from a CSS background-image value, e.g. `url("https://...")`. */
function parseBgImageUrls(bgImage: string): string[] {
  const urls: string[] = [];
  const re = /url\(['"]?([^'")\s]+)['"]?\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(bgImage)) !== null) {
    if (m[1] && !m[1].startsWith('data:')) urls.push(m[1]);
  }
  return urls;
}

async function buildImageCache(
  root: Element,
  fetchImage?: (src: string) => Promise<string | null>,
): Promise<Map<string, string>> {
  const seen = new Set<string>();
  const cache = new Map<string, string>();

  const enqueue = async (src: string) => {
    if (!src || src.startsWith('data:') || seen.has(src)) return;
    seen.add(src);
    const dataUrl = await resolveImageAsDataUrl(src, fetchImage);
    if (dataUrl) cache.set(src, dataUrl);
  };

  const tasks: Promise<void>[] = [];

  // <img src>
  for (const img of Array.from(root.querySelectorAll('img[src]'))) {
    tasks.push(enqueue((img as HTMLImageElement).src));
  }

  // <img srcset> and <picture><source srcset>
  for (const el of Array.from(root.querySelectorAll('img[srcset], source[srcset]'))) {
    const srcset = el.getAttribute('srcset') ?? '';
    for (const part of srcset.split(',')) {
      const url = part.trim().split(/\s+/)[0];
      if (url) tasks.push(enqueue(url));
    }
  }

  // inline style background-image
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[style*="background"]'))) {
    for (const url of parseBgImageUrls(el.style.backgroundImage)) {
      tasks.push(enqueue(url));
    }
  }

  await Promise.allSettled(tasks);
  return cache;
}

function makeInlineImagesPlugin(cache: Map<string, string>): SnapdomPlugin {
  return {
    name: 'inline-images',
    afterClone(ctx) {
      const clone = ctx.clone;
      if (!clone) return;

      // <img src>
      clone.querySelectorAll('img[src]').forEach((img) => {
        const el = img as HTMLImageElement;
        // Try resolved absolute src first, then raw attribute (detached clones may differ).
        const dataUrl = cache.get(el.src) ?? cache.get(el.getAttribute('src') ?? '');
        if (dataUrl) el.src = dataUrl;
      });

      // <img srcset> and <picture><source srcset>
      clone.querySelectorAll('img[srcset], source[srcset]').forEach((el) => {
        const srcset = el.getAttribute('srcset') ?? '';
        const replaced = srcset
          .split(',')
          .map((part) => {
            const [url, ...descriptors] = part.trim().split(/\s+/);
            const dataUrl = cache.get(url);
            return dataUrl ? [dataUrl, ...descriptors].join(' ') : part;
          })
          .join(', ');
        if (replaced !== srcset) el.setAttribute('srcset', replaced);
      });

      // inline style background-image
      clone.querySelectorAll<HTMLElement>('[style*="background"]').forEach((el) => {
        const bg = el.style.backgroundImage;
        if (!bg) return;
        const replaced = bg.replace(/url\(['"]?([^'")\s]+)['"]?\)/g, (match, url) => {
          const dataUrl = cache.get(url);
          return dataUrl ? `url("${dataUrl}")` : match;
        });
        if (replaced !== bg) el.style.backgroundImage = replaced;
      });
    },
  };
}

export async function captureElementToPng(
  el: Element,
  fetchImage?: (src: string) => Promise<string | null>,
): Promise<Blob> {
  const cache = await buildImageCache(el, fetchImage);
  const plugins: SnapdomPlugin[] = cache.size > 0 ? [makeInlineImagesPlugin(cache)] : [];
  return snapdom.toBlob(el, { type: 'png', backgroundColor: '#ffffff', plugins });
}

export async function captureIframeContentToPng(
  iframe: HTMLIFrameElement,
  fetchImage?: (src: string) => Promise<string | null>,
): Promise<Blob> {
  const root = iframe.contentDocument?.documentElement;
  if (!root) throw new Error('iframe document not accessible');
  return captureElementToPng(root, fetchImage);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function copyBlobToClipboard(blob: Blob): Promise<void> {
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
}

export function buildExportFilename(templateKey: string, channel: string, previewMode: string): string {
  const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '');
  return `${templateKey}__${channel.toLowerCase()}_${previewMode}__${ts}.png`;
}

/** Orchestrates a single export: captures a blob then either downloads or copies it. */
export async function performExport(
  action: ExportAction,
  getBlob: () => Promise<Blob>,
  filename: string,
): Promise<ExportResult> {
  let blob: Blob;
  try {
    blob = await getBlob();
  } catch {
    return { ok: false, reason: 'capture-failed' };
  }

  try {
    if (action === 'download') {
      downloadBlob(blob, filename);
      return { ok: true, filename };
    } else {
      await copyBlobToClipboard(blob);
      return { ok: true };
    }
  } catch (err: unknown) {
    if (action === 'clipboard') {
      // Check by name so it works across frames and jsdom environments.
      const isDenied = (err as any)?.name === 'NotAllowedError';
      return { ok: false, reason: isDenied ? 'clipboard-denied' : 'capture-failed' };
    }
    return { ok: false, reason: 'capture-failed' };
  }
}
